import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Mic,
  Square,
  Pause,
  Play,
  Upload,
  Loader2,
  Trash2,
  FileAudio,
  Wand2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { useAudioTranscription } from '../hooks/useAudioTranscription';
import { useMedicalRecordAutoFill } from '../hooks/useMedicalRecordAutoFill';

/**
 * ConsultationAudioRecorder Component
 *
 * Complete audio recording and auto-fill solution for consultation forms.
 * Integrates recording, transcription, and AI-powered data extraction.
 *
 * @param {Function} onAutoFillData - Callback with extracted data to fill form
 * @param {Function} onAudioChange - Callback when audio changes (blob, url)
 * @param {Function} onTranscriptionChange - Callback when transcription changes
 * @param {Object} currentFormData - Current form data for merging
 * @param {Object} currentTreatment - Current treatment data for merging
 * @param {boolean} disabled - Disable all controls
 * @param {string} initialAudioUrl - Initial audio URL for playback (edit mode)
 * @param {string} initialTranscription - Initial transcription text (edit mode)
 * @param {boolean} readOnly - If true, only show playback (no recording)
 */
export default function ConsultationAudioRecorder({
  onAutoFillData,
  onAudioChange,
  onTranscriptionChange,
  currentFormData = {},
  currentTreatment = {},
  disabled = false,
  initialAudioUrl = null,
  initialTranscription = '',
  readOnly = false
}) {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(initialAudioUrl);
  const [isExpanded, setIsExpanded] = useState(true);

  // Hooks - MUST be declared before useEffect that uses them
  const {
    isTranscribing,
    transcription,
    transcribe,
    clearTranscription,
    setTranscription
  } = useAudioTranscription();

  // Initialize with initial values if provided (edit mode)
  useEffect(() => {
    if (initialAudioUrl) {
      setAudioUrl(initialAudioUrl);
    }
  }, [initialAudioUrl]);

  useEffect(() => {
    if (initialTranscription && setTranscription) {
      setTranscription(initialTranscription);
    }
  }, [initialTranscription, setTranscription]);

  const {
    isExtracting,
    extractedData,
    extractMedicalData,
    mapToFormFields,
    clearExtractedData
  } = useMedicalRecordAutoFill();

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      streamRef.current = stream;
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        // Notify parent of audio change
        if (onAudioChange) {
          onAudioChange(blob, url);
        }

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Grabaci\u00f3n iniciada');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('No se pudo acceder al micr\u00f3fono. Verifica los permisos del navegador.');
    }
  };

  // Pause/Resume recording
  const togglePause = () => {
    if (!mediaRecorderRef.current) return;

    if (isPaused) {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      setIsPaused(false);
    } else {
      mediaRecorderRef.current.pause();
      if (timerRef.current) clearInterval(timerRef.current);
      setIsPaused(true);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
      setIsRecording(false);
      setIsPaused(false);
      toast.success('Grabaci\u00f3n finalizada');
    }
  };

  // Clear everything
  const clearAll = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    clearTranscription();
    clearExtractedData();
    audioChunksRef.current = [];
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedExtensions = ['mp3', 'mpeg', 'wav', 'm4a', 'ogg', 'flac', 'webm', 'mp4'];
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      toast.error('Formato no soportado. Use MP3, WAV, M4A, OGG, FLAC o WebM.');
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error('Archivo muy grande. M\u00e1ximo 25MB.');
      return;
    }

    setAudioBlob(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    // Notify parent of audio change
    if (onAudioChange) {
      onAudioChange(file, url);
    }
    toast.success(`Archivo cargado: ${file.name}`);
  };

  // Handle transcription
  const handleTranscribe = async () => {
    if (!audioBlob) return;

    const text = await transcribe(audioBlob);
    if (text) {
      // Notify parent of transcription change
      if (onTranscriptionChange) {
        onTranscriptionChange(text);
      }
      toast.success('Transcripci\u00f3n completada');
    }
  };

  // Handle auto-fill
  const handleAutoFill = async () => {
    if (!transcription) {
      toast.error('Primero debes transcribir el audio');
      return;
    }

    const data = await extractMedicalData(transcription);
    if (data) {
      const mappedData = mapToFormFields(data, currentFormData, currentTreatment);

      if (onAutoFillData) {
        onAutoFillData(mappedData);
      }

      toast.success('Datos extra\u00eddos y aplicados al formulario');
    }
  };

  const isProcessing = isTranscribing || isExtracting;

  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
      {/* Header */}
      <div
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Mic className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-blue-900">Grabación y Auto-llenado con IA</h3>
            <p className="text-xs text-blue-600">Graba la consulta y completa automáticamente el formulario</p>
          </div>
        </div>
        <Button variant="ghost" size="sm">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Recording Controls - only show if not readOnly */}
          {!readOnly && (
            <div className="flex flex-wrap items-center gap-3">
              {!isRecording && !audioBlob && !audioUrl && (
                <>
                  <Button
                    onClick={startRecording}
                    disabled={disabled || isProcessing}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Grabar
                  </Button>

                  <div className="relative">
                    <input
                      type="file"
                      accept=".mp3,.wav,.m4a,.ogg,.flac,.webm,.mp4,audio/*"
                      onChange={handleFileUpload}
                      disabled={disabled || isProcessing}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <Button variant="outline" disabled={disabled || isProcessing}>
                      <Upload className="w-4 h-4 mr-2" />
                      Subir Audio
                    </Button>
                  </div>
                </>
              )}

              {isRecording && (
                <>
                  <Button
                    onClick={togglePause}
                    variant="outline"
                    className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                  >
                    {isPaused ? (
                      <><Play className="w-4 h-4 mr-2" />Reanudar</>
                    ) : (
                      <><Pause className="w-4 h-4 mr-2" />Pausar</>
                    )}
                  </Button>

                  <Button onClick={stopRecording} className="bg-gray-700 hover:bg-gray-800 text-white">
                    <Square className="w-4 h-4 mr-2" />
                    Detener
                  </Button>

                  <div className={`flex items-center gap-2 px-3 py-2 rounded-full ${isPaused ? 'bg-yellow-100' : 'bg-red-100'}`}>
                    <span className={`w-3 h-3 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`} />
                    <span className={`font-mono text-sm ${isPaused ? 'text-yellow-700' : 'text-red-700'}`}>
                      {formatTime(recordingTime)}
                    </span>
                  </div>
                </>
              )}

              {(audioBlob || audioUrl) && !isRecording && (
                <Button
                  onClick={clearAll}
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  disabled={isProcessing}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Limpiar
                </Button>
              )}
            </div>
          )}

          {/* Audio Player & Actions */}
          {audioUrl && !isRecording && (
            <div className="space-y-3 p-3 bg-white rounded-lg border">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FileAudio className="w-4 h-4" />
                <span>Audio {readOnly ? 'de la consulta' : 'listo'}</span>
                {recordingTime > 0 && <span className="text-gray-400">({formatTime(recordingTime)})</span>}
              </div>

              <audio controls src={audioUrl} className="w-full h-10" />

              {!readOnly && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleTranscribe}
                    disabled={isProcessing || disabled || !audioBlob}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isTranscribing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Transcribiendo...</>
                    ) : (
                      <><FileAudio className="w-4 h-4 mr-2" />Transcribir</>
                    )}
                  </Button>

                  {transcription && (
                    <Button
                      onClick={handleAutoFill}
                      disabled={isProcessing || disabled}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      {isExtracting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Extrayendo...</>
                      ) : (
                        <><Wand2 className="w-4 h-4 mr-2" />Auto-rellenar</>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Transcription Display/Edit */}
          {transcription && (
            <div className="p-3 bg-white rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-green-800 text-sm">Transcripci\u00f3n:</h4>
                <span className="text-xs text-gray-400">{transcription.length} caracteres</span>
              </div>
              {readOnly ? (
                <p className="text-sm text-gray-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                  {transcription}
                </p>
              ) : (
                <textarea
                  className="w-full text-sm text-gray-700 p-2 border rounded resize-none max-h-32 overflow-y-auto"
                  value={transcription}
                  onChange={(e) => {
                    setTranscription(e.target.value);
                    if (onTranscriptionChange) {
                      onTranscriptionChange(e.target.value);
                    }
                  }}
                  rows={4}
                  placeholder="Editar transcripción..."
                />
              )}
            </div>
          )}

          {/* Extracted Data Preview */}
          {extractedData && (
            <div className="p-3 bg-white rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-800 text-sm mb-2">Datos extra\u00eddos:</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {extractedData.motivo_consulta && (
                  <div>
                    <span className="font-medium text-gray-600">Motivo:</span>
                    <p className="text-gray-800 truncate">{extractedData.motivo_consulta}</p>
                  </div>
                )}
                {extractedData.diagnosis && (
                  <div>
                    <span className="font-medium text-gray-600">Diagn\u00f3stico:</span>
                    <p className="text-gray-800 truncate">{extractedData.diagnosis}</p>
                  </div>
                )}
                {extractedData.medicamentos?.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-600">Medicamentos:</span>
                    <p className="text-gray-800">{extractedData.medicamentos.length} encontrados</p>
                  </div>
                )}
                {extractedData.laboratorio?.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-600">Ex\u00e1menes Lab:</span>
                    <p className="text-gray-800">{extractedData.laboratorio.length} encontrados</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
